import axiosInstance from "./axiosInstance";
const MERCHANT_BASE_URL = "/merchant";
const MERCHANT_CHANNEL_BALANCE_URL = "/merchant/channelBalance";
const MERCHANT_FEATURES_URL = "/features";
const MERCHANT_CREATE_URL = "/merchant/new";
const MERCHANT_PROFILE_BASE_URL = "/merchant/profile";
const MERCHANTS_ACCOUNTS_BASE_URL = "/merchant/accounts";
const MERCHANT_MOBILE_BASE_URL = "/merchant/mobile-money";
const MERCHANT_MOBILE_HAKIKISHA_URL = "/merchant/mobile-money/hakikisha";
const MERCHANT_BANK_BASE_URL = "/merchant/bank-account";
const MERCHANT_WALLET_BASE_URL = "/merchant/wallet-account";
const MERCHANT_ECONOMIC_SECTORS_URL = "/merchant/settings/economic-sectors";
const MERCHANT_PRODUCT_TRANSACTION_CODES_URL =
  "/merchant/settings/product-transaction-codes";
const MERCHANT_PRODUCT_OWNERSHIPS_URL = "/merchant/settings/product-ownerships";
const MERCHANT_PRODUCT_CATEGORIES_URL = "/merchant/settings/product-categories";
const MERCHANT_GENDERS_URL = "/merchant/settings/genders";
const MERCHANT_SEGMENTS_URL = "/merchant/settings/segments";
const MERCHANT_DISBURSEMENTS_CHARGES_URL = "/merchant/disbursements/charges";
const MERCHANT_DISBURSEMENTS_LIMITS_URL = "/merchant/disbursements/limits";
const MERCHANT_DISBURSEMENTS_INITIATE_URL = "/merchant/disbursements/initiate";
const MERCHANT_DISBURSEMENTS_AUTHORIZE_URL =
  "/merchant/disbursements/authorize";
const MERCHANT_DISBURSEMENTS_BASE_URL = "/merchant/disbursements";
const MANAGEMENT_MERCHANTS_FILTER_URL = "/management/filters/merchants";

const merchantService = {
  getAllMerchants: async ({
    merchantSegment,
    searchQuery,
    page = 0,
    size = 10,
  } = {}) => {
    const params = { page, size };
    if (merchantSegment) params.merchantSegment = merchantSegment;
    if (searchQuery) params.searchQuery = searchQuery;
    const response = await axiosInstance.get(MERCHANT_BASE_URL, { params });
    return response.data;
  },

  getMerchantFilters: async (searchQuery) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append("searchQuery", searchQuery);
    const response = await axiosInstance.get(
      `${MANAGEMENT_MERCHANTS_FILTER_URL}?${params.toString()}`,
    );
    return response.data;
  },

  getCurrentMerchantProfile: async () => {
    const response = await axiosInstance.get(MERCHANT_PROFILE_BASE_URL);
    return response.data;
  },

  getMerchantProfileById: async (merchantId) => {
    const response = await axiosInstance.get(
      `${MERCHANT_BASE_URL}/${merchantId}`,
    );
    return response.data;
  },

  getMerchantPaymentAccounts: async () => {
    const response = await axiosInstance.get(MERCHANTS_ACCOUNTS_BASE_URL);
    return response.data;
  },

  addMobileMoneyAccount: async (accountData) => {
    const response = await axiosInstance.post(
      MERCHANT_MOBILE_BASE_URL,
      accountData,
    );
    return response.data;
  },

  verifyMobileMoneyAccount: async ({ msisdn, provider }) => {
    const response = await axiosInstance.post(MERCHANT_MOBILE_HAKIKISHA_URL, {
      msisdn,
      provider,
    });
    return response.data;
  },

  addBankAccount: async (accountData) => {
    const response = await axiosInstance.post(
      MERCHANT_BANK_BASE_URL,
      accountData,
    );
    return response.data;
  },

  addPaykitWallet: async (accountData) => {
    const response = await axiosInstance.post(
      MERCHANT_WALLET_BASE_URL,
      accountData,
    );
    return response.data;
  },

  createMerchantInstitution: async (accountData) => {
    const response = await axiosInstance.post(MERCHANT_CREATE_URL, accountData);
    return response.data;
  },

  getEconomicSectors: async () => {
    const response = await axiosInstance.get(MERCHANT_ECONOMIC_SECTORS_URL);
    return response.data;
  },

  getProductTransactionCodes: async () => {
    const response = await axiosInstance.get(
      MERCHANT_PRODUCT_TRANSACTION_CODES_URL,
    );
    return response.data;
  },

  getProductOwnerships: async () => {
    const response = await axiosInstance.get(MERCHANT_PRODUCT_OWNERSHIPS_URL);
    return response.data;
  },

  getProductCategories: async () => {
    const response = await axiosInstance.get(MERCHANT_PRODUCT_CATEGORIES_URL);
    return response.data;
  },

  getGenders: async () => {
    const response = await axiosInstance.get(MERCHANT_GENDERS_URL);
    return response.data;
  },

  getMerchantSegments: async () => {
    const response = await axiosInstance.get(MERCHANT_SEGMENTS_URL);
    return response.data;
  },

  editMerchantInstitution: async (merchantId, merchantData) => {
    const response = await axiosInstance.put(
      `${MERCHANT_BASE_URL}/${merchantId}`,
      merchantData,
    );
    return response.data;
  },

  editCurrentMerchantInstitution: async (merchantData) => {
    const response = await axiosInstance.put(
      `${MERCHANT_PROFILE_BASE_URL}`,
      merchantData,
    );
    return response.data;
  },

  addMerchantInstitutionMobileMoneyAccount: async (merchantId, accountData) => {
    const response = await axiosInstance.post(
      `${MERCHANT_BASE_URL}/${merchantId}/mobile-money`,
      accountData,
    );
    return response.data;
  },

  updateMerchantLogo: async (data) => {
    const response = await axiosInstance.put(`${MERCHANT_BASE_URL}/logo`, data);
    return response.data;
  },

  getMerchantWalletBalance: async () => {
    const response = await axiosInstance.get(`/merchant/balance`);
    return response.data;
  },

  getMerchantChannelBalances: async (merchantId) => {
    const params = merchantId ? { merchantId } : undefined;
    const response = await axiosInstance.get(MERCHANT_CHANNEL_BALANCE_URL, {
      params,
    });
    return response.data;
  },

  toggleMerchantStatus: async (merchantId, actionType) => {
    const response = await axiosInstance.post(
      `${MERCHANT_BASE_URL}/${merchantId}/actions`,
      { actionType },
    );
    return response.data;
  },

  getMerchantDisbursementsCharges: async (payload) => {
    const response = await axiosInstance.post(
      MERCHANT_DISBURSEMENTS_CHARGES_URL,
      payload,
    );
    return response.data;
  },

  getMerchantDisbursementLimits: async (payload) => {
    const response = await axiosInstance.post(
      MERCHANT_DISBURSEMENTS_LIMITS_URL,
      payload,
    );
    return response.data;
  },

  initiateMerchantWithdrawal: async (payload) => {
    const response = await axiosInstance.post(
      MERCHANT_DISBURSEMENTS_INITIATE_URL,
      payload,
    );
    return response.data;
  },

  resendDisbursementOtp: async (transactionId) => {
    const response = await axiosInstance.patch(
      `${MERCHANT_DISBURSEMENTS_BASE_URL}/${transactionId}/resend-otp`,
    );
    return response.data;
  },

  authorizeDisbursement: async (payload) => {
    const response = await axiosInstance.post(
      MERCHANT_DISBURSEMENTS_AUTHORIZE_URL,
      payload,
    );
    return response.data;
  },

  getDisbursement: async (transactionId) => {
    const response = await axiosInstance.get(
      `${MERCHANT_DISBURSEMENTS_BASE_URL}/${transactionId}`,
    );
    return response.data;
  },

  getMerchantFeatureFlags: async () => {
    const response = await axiosInstance.get(MERCHANT_FEATURES_URL);
    return response.data;
  },
};

export default merchantService;
